import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Check,
  ChevronDown,
  ChevronUp,
  LogOut,
  UtensilsCrossed,
  Coffee,
  Pizza as PizzaIcon,
  X,
  Trash2,
  Info,
} from 'lucide-react'
import { BarEventForm } from '@/components/reserved/BarEventForm'
import { WeeklyDishesManager } from '@/components/reserved/WeeklyDishesManager'
import { MenuItemsManager } from '@/components/reserved/MenuItemsManager'
import { BarMenuItemsManager } from '@/components/reserved/BarMenuItemsManager'
import { BookingDetailModal } from '@/components/reserved/BookingDetailModal'
import { cn } from '@/lib/utils'
import { AUTH_KEY } from '@/lib/adminAuth'
import { formatDateInput, getAvailableSlots, isDateClosed, parseTime } from '@/lib/booking'
import {
  cancelBooking,
  confirmBooking,
  deleteBooking,
  getBookings,
  type Booking,
} from '@/lib/mockBookings'

type Zone = 'maia' | 'bar' | 'pizza'
type DayPeriod = 'lunch' | 'dinner'

export function ReservedArea() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const currentLang = (lang as 'it' | 'en') || 'it'

  const [activeZone, setActiveZone] = useState<Zone>('maia')
  const [bookings, setBookings] = useState<Booking[]>([])

  useEffect(() => {
    if (localStorage.getItem(AUTH_KEY) !== '1') {
      navigate(`/${lang}/login`)
      return
    }
    const refresh = () => setBookings(getBookings())
    refresh()
    const interval = setInterval(refresh, 2000)
    return () => clearInterval(interval)
  }, [navigate, lang])

  const handleLogout = () => {
    localStorage.removeItem(AUTH_KEY)
    navigate(`/${lang}/login`)
  }

  const handleConfirm = (id: string) => {
    confirmBooking(id)
    setBookings(getBookings())
  }

  const handleCancel = (id: string) => {
    cancelBooking(id)
    setBookings(getBookings())
  }

  const handleDelete = (id: string) => {
    deleteBooking(id)
    setBookings(getBookings())
  }

  const zones: { key: Zone; label: string; icon: React.ElementType }[] = [
    { key: 'maia', label: t('zone.maia.title'), icon: UtensilsCrossed },
    { key: 'bar', label: t('zone.bar.title'), icon: Coffee },
    { key: 'pizza', label: t('zone.pizza.title'), icon: PizzaIcon },
  ]

  const zoneBackground: Record<Zone, string> = {
    maia: 'bg-maia',
    bar: 'bg-bar',
    pizza: 'bg-pizza',
  }
  const isLightBg = activeZone === 'bar'

  return (
    <main className={cn('min-h-screen px-4 py-10 sm:px-6 lg:px-8', zoneBackground[activeZone])}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className={cn(
              'font-display text-3xl font-bold md:text-4xl',
              isLightBg ? 'text-ink' : 'text-white'
            )}>
              {t('dashboard.title')}
            </h1>
            <p className={cn(
              'mt-3 max-w-2xl text-lg',
              isLightBg ? 'text-ink/80' : 'text-white/80'
            )}>
              {t('dashboard.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className={cn(
              'inline-flex items-center gap-2 self-start rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors',
              isLightBg
                ? 'border-ink/20 bg-ink/10 text-ink hover:bg-ink/20'
                : 'border-white/20 bg-white/10 text-white hover:bg-white/20'
            )}
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('logout')}
          </button>
        </div>

        <nav
          aria-label={t('aria.zoneNavigation')}
          className={cn('mb-8 flex gap-2 border-b', isLightBg ? 'border-ink/20' : 'border-white/20')}
        >
          {zones.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveZone(key)}
              className={cn(
                'inline-flex items-center gap-2 border-b-2 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors',
                activeZone === key
                  ? isLightBg
                    ? 'border-ink text-ink'
                    : 'border-white text-white'
                  : isLightBg
                    ? 'border-transparent text-ink/70 hover:text-ink'
                    : 'border-transparent text-white/70 hover:text-white'
              )}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>

        <div>
          {activeZone === 'maia' && (
            <div className="space-y-10">
              <DailyBookingsSelector
                bookings={bookings}
                lang={currentLang}
                t={t}
                isLightBg={isLightBg}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                onDelete={handleDelete}
              />
              <div>
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-white/90">
                  {t('zone.menuTitle')}
                </p>
                <div className="space-y-10">
                  <section>
                    <WeeklyDishesManager />
                  </section>
                  <section>
                    <MenuItemsManager category="starter" zonePrefix="zone.starters" />
                  </section>
                  <section>
                    <MenuItemsManager category="dessert" zonePrefix="zone.desserts" />
                  </section>
                </div>
              </div>
            </div>
          )}
          {activeZone === 'bar' && (
            <div className="space-y-10">
              <section>
                <p className="mb-8 text-xs font-bold uppercase tracking-[0.15em] text-ink/90">
                  {t('zone.event.subtitle')}
                </p>
                <BarEventForm />
              </section>
              <div>
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-ink/90">
                  {t('zone.menuTitle')}
                </p>
                <div className="space-y-10">
                  <section>
                    <BarMenuItemsManager category="breakfast" zonePrefix="zone.barBreakfast" />
                  </section>
                  <section>
                    <BarMenuItemsManager category="lunch" zonePrefix="zone.barLunch" />
                  </section>
                </div>
              </div>
            </div>
          )}
          {activeZone === 'pizza' && <Placeholder t={t} />}
        </div>
      </div>
    </main>
  )
}

function DailyBookingsSelector({
  bookings,
  lang,
  t,
  isLightBg,
  onConfirm,
  onCancel,
  onDelete,
}: {
  bookings: Booking[]
  lang: 'it' | 'en'
  t: (key: string) => string
  isLightBg: boolean
  onConfirm: (id: string) => void
  onCancel: (id: string) => void
  onDelete: (id: string) => void
}) {
  const locale = lang === 'it' ? 'it-IT' : 'en-GB'

  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(timer)
  }, [])

  const days = useMemo(() => {
    const today = new Date(now)
    const list: Date[] = []
    for (let i = 0; i < 7; i++) {
      const d = new Date(today)
      d.setDate(today.getDate() + i)
      list.push(d)
    }
    return list
  }, [now])

  const firstOpenDate = (list: Date[]) => {
    const firstOpen = list.findIndex((d) => !isDateClosed(d))
    return formatDateInput(firstOpen >= 0 ? list[firstOpen] : list[0])
  }

  const [selectedDate, setSelectedDate] = useState(() => firstOpenDate(days))

  useEffect(() => {
    setSelectedDate((prev) =>
      days.some((d) => formatDateInput(d) === prev)
        ? prev
        : firstOpenDate(days)
    )
  }, [days])

  const selectedIndex = days.findIndex((d) => formatDateInput(d) === selectedDate)
  const selectedDay = days[selectedIndex] ?? days[0]

  const getPeriod = (time: string): DayPeriod => {
    const minutes = parseTime(time)
    // Lunch slots end at 14:30, dinner starts at 19:00. 17:00 is a safe threshold.
    return minutes < 17 * 60 ? 'lunch' : 'dinner'
  }

  const selectedDateStr = formatDateInput(selectedDay)
  const dayBookings = bookings.filter((b) => b.date === selectedDateStr)
  const lunch = dayBookings.filter((b) => getPeriod(b.time) === 'lunch')
  const dinner = dayBookings.filter((b) => getPeriod(b.time) === 'dinner')

  const availableSlots = useMemo(() => getAvailableSlots(selectedDay), [selectedDay])
  const hasLunch =
    availableSlots.some((s) => s.period === 'lunch') || lunch.length > 0
  const hasDinner =
    availableSlots.some((s) => s.period === 'dinner') || dinner.length > 0

  const selectedClosed = isDateClosed(selectedDay)

  return (
    <div className="space-y-6">
      <div>
        <p
          className={cn(
            'mb-3 text-xs font-bold uppercase tracking-[0.15em]',
            isLightBg ? 'text-ink/90' : 'text-white/90'
          )}
        >
          {t('zone.bookings.selectDay')}
        </p>
        <div className="flex gap-3 overflow-x-auto p-2 pb-3 -m-2">
          {days.map((date, index) => {
            const dateStr = formatDateInput(date)
            const count = bookings.filter(
              (b) => b.date === dateStr && b.status === 'pending'
            ).length
            const isSelected = index === selectedIndex
            const closed = isDateClosed(date)

            return (
              <button
                key={dateStr}
                type="button"
                disabled={closed}
                aria-disabled={closed}
                onClick={() => setSelectedDate(dateStr)}
                className={cn(
                  'flex min-w-[88px] flex-col items-center rounded-xl border px-4 py-3 text-center transition-all',
                  closed
                    ? isSelected
                      ? 'border-pizza bg-pizza text-white shadow-lg ring-2 ring-white/40'
                      : 'border-pizza/30 bg-pizza/10 text-pizza-dark opacity-70 cursor-not-allowed'
                    : isSelected
                      ? 'border-maia-dark bg-maia-dark text-white shadow-lg ring-2 ring-white/40'
                      : 'border-cord bg-white text-ink hover:border-maia'
                )}
              >
                <span
                  className={cn(
                    'text-[10px] font-bold uppercase tracking-wider',
                    closed && !isSelected
                      ? 'text-pizza-dark'
                      : isSelected && !closed
                        ? 'text-white/80'
                        : 'text-muted'
                  )}
                >
                  {date.toLocaleDateString(locale, { weekday: 'short' })}
                </span>
                <span className="font-display text-2xl font-bold leading-none">
                  {date.getDate()}
                </span>
                {closed ? (
                  <span
                    className={cn(
                      'mt-1 text-[10px] font-bold uppercase tracking-wider',
                      isSelected ? 'text-white/90' : 'text-pizza-dark'
                    )}
                  >
                    {t('zone.bookings.closed')}
                  </span>
                ) : count > 0 ? (
                  <span
                    className={cn(
                      'mt-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold',
                      isSelected
                        ? 'bg-white/20 text-white'
                        : 'bg-maia/10 text-maia-dark'
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      </div>

      <section className="overflow-hidden rounded-2xl border border-cord bg-white">
        <header className="flex flex-wrap items-center justify-between gap-3 bg-maia-soft px-5 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-display text-2xl font-bold text-ink">
              {selectedDay.toLocaleDateString(locale, { weekday: 'long' })}
            </span>
            <span className="text-lg capitalize text-muted">
              {selectedDay.toLocaleDateString(locale, {
                day: 'numeric',
                month: 'long',
              })}
            </span>
            {selectedClosed && (
              <span className="rounded-full bg-pizza px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                {t('zone.bookings.closed')}
              </span>
            )}
          </div>
          {!selectedClosed && (
            <span className="text-sm text-muted">
              {dayBookings.length}{' '}
              {dayBookings.length === 1
                ? t('zone.bookings.singular') ?? 'prenotazione'
                : t('zone.bookings.plural') ?? 'prenotazioni'}
            </span>
          )}
        </header>

        {selectedClosed ? (
          <div className="flex h-48 flex-col items-center justify-center gap-3 px-5 text-center">
            <span className="rounded-full bg-pizza/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-pizza-dark">
              {t('zone.bookings.closed')}
            </span>
            <p className="text-sm text-muted">{t('zone.bookings.closedMessage')}</p>
          </div>
        ) : (
          <div className="divide-y divide-cord">
            {hasLunch && (
              <PeriodTable
                title={t('zone.bookings.lunch')}
                bookings={lunch}
                t={t}
                onConfirm={onConfirm}
                onCancel={onCancel}
                onDelete={onDelete}
              />
            )}
            {hasDinner && (
              <PeriodTable
                title={t('zone.bookings.dinner')}
                bookings={dinner}
                t={t}
                onConfirm={onConfirm}
                onCancel={onCancel}
                onDelete={onDelete}
              />
            )}
          </div>
        )}
      </section>
    </div>
  )
}

function PeriodTable({
  title,
  bookings,
  t,
  onConfirm,
  onCancel,
  onDelete,
}: {
  title: string
  bookings: Booking[]
  t: (key: string) => string
  onConfirm: (id: string) => void
  onCancel: (id: string) => void
  onDelete: (id: string) => void
}) {
  const [sortKey, setSortKey] = useState<'time' | 'guests' | 'status' | null>(
    'time'
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const [isOpen, setIsOpen] = useState(true)
  const [detailBooking, setDetailBooking] = useState<Booking | null>(null)

  const totalGuests = bookings.reduce((sum, b) => sum + b.guests, 0)

  const sortedBookings = useMemo(() => {
    if (!sortKey) return bookings
    const list = [...bookings].sort((a, b) => {
      if (sortKey === 'time') return parseTime(a.time) - parseTime(b.time)
      if (sortKey === 'guests') return a.guests - b.guests
      const order = { confirmed: 0, pending: 1, cancelled: 2 } as const
      return order[a.status] - order[b.status]
    })
    return sortDir === 'desc' ? list.reverse() : list
  }, [bookings, sortKey, sortDir])

  const handleSort = (key: 'time' | 'guests' | 'status') => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const SortIndicator = ({
    active,
    dir,
  }: {
    active: boolean
    dir: 'asc' | 'desc'
  }) => (
    <span
      className={cn(
        'ml-1 text-[10px]',
        active ? 'text-maia-dark' : 'text-muted/50'
      )}
      aria-hidden="true"
    >
      {dir === 'asc' ? '↑' : '↓'}
    </span>
  )

  return (
    <div className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-display text-xl font-bold text-maia-dark">
            {title}
          </h3>
          {bookings.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-maia/15 px-3 py-1 text-xs font-bold text-maia-dark ring-1 ring-maia/30">
              <span className="font-display text-sm leading-none">{totalGuests}</span>
              <span>
                {totalGuests === 1
                  ? t('zone.bookings.coverSingular') ?? 'coperto'
                  : t('zone.bookings.coverPlural') ?? 'coperti'}
              </span>
            </span>
          )}
        </div>
        {bookings.length > 0 && (
          <button
            type="button"
            onClick={() => setIsOpen((v) => !v)}
            aria-expanded={isOpen}
            aria-label={isOpen ? t('zone.bookings.collapse') : t('zone.bookings.expand')}
            className="inline-flex items-center gap-1 rounded-full bg-cream px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ink transition-colors hover:bg-cord"
          >
            {isOpen ? (
              <>
                <span>{t('zone.bookings.collapse')}</span>
                <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
              </>
            ) : (
              <>
                <span>{t('zone.bookings.expand')}</span>
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </>
            )}
          </button>
        )}
      </div>

      {isOpen && (bookings.length === 0 ? (
        <p className="text-sm text-muted">{t('zone.bookings.empty')}</p>
      ) : (
        <>
        <div className="sm:hidden space-y-3">
          {sortedBookings.map((booking) => (
            <MobileBookingCard
              key={booking.id}
              booking={booking}
              t={t}
              onConfirm={onConfirm}
              onCancel={onCancel}
              onDelete={onDelete}
              onShowDetail={setDetailBooking}
            />
          ))}
        </div>

        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-cord text-xs uppercase tracking-wider text-muted">
                <th className="py-2 pr-4 font-semibold">
                  <button
                    type="button"
                    onClick={() => handleSort('time')}
                    className={cn(
                      'inline-flex items-center transition-colors hover:text-maia-dark',
                      sortKey === 'time' && 'text-maia-dark'
                    )}
                  >
                    {t('zone.bookings.time')}
                    {sortKey === 'time' && (
                      <SortIndicator active dir={sortDir} />
                    )}
                  </button>
                </th>
                <th className="py-2 pr-4 font-semibold">
                  {t('zone.bookings.name')}
                </th>
                <th className="py-2 pr-4 font-semibold">
                  {t('zone.bookings.phone')}
                </th>
                <th className="py-2 pr-4 font-semibold">
                  <button
                    type="button"
                    onClick={() => handleSort('guests')}
                    className={cn(
                      'inline-flex items-center transition-colors hover:text-maia-dark',
                      sortKey === 'guests' && 'text-maia-dark'
                    )}
                  >
                    {t('zone.bookings.guests')}
                    {sortKey === 'guests' && (
                      <SortIndicator active dir={sortDir} />
                    )}
                  </button>
                </th>
                <th className="py-2 pr-4 font-semibold">
                  <button
                    type="button"
                    onClick={() => handleSort('status')}
                    className={cn(
                      'inline-flex items-center transition-colors hover:text-maia-dark',
                      sortKey === 'status' && 'text-maia-dark'
                    )}
                  >
                    {t('zone.bookings.status')}
                    {sortKey === 'status' && (
                      <SortIndicator active dir={sortDir} />
                    )}
                  </button>
                </th>
                <th className="py-2 pr-4 font-semibold">
                  {t('zone.bookings.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cord">
              {sortedBookings.map((booking) => (
                <tr key={booking.id} className="transition-colors hover:bg-cream/60">
                  <td className="py-3 pr-4 font-medium text-ink">
                    {booking.time}
                  </td>
                  <td className="py-3 pr-4 text-ink">{booking.name}</td>
                  <td className="py-3 pr-4">
                    <a
                      href={`tel:${booking.phone}`}
                      className="text-maia-dark hover:text-pizza hover:underline"
                    >
                      {booking.phone}
                    </a>
                  </td>
                  <td className="py-3 pr-4 text-ink">{booking.guests}</td>
                  <td className="py-3 pr-4">
                    {(() => {
                      const statusDotClass =
                        booking.status === 'confirmed'
                          ? 'bg-maia'
                          : booking.status === 'cancelled'
                            ? 'bg-pizza'
                            : 'bg-gray-400'
                      return (
                        <span className="inline-flex items-center gap-2 text-sm text-ink">
                          <span
                            className={cn('h-2 w-2 rounded-full', statusDotClass)}
                            aria-hidden="true"
                          />
                          {booking.status === 'confirmed'
                            ? t('status.confirmed')
                            : booking.status === 'cancelled'
                              ? t('status.cancelled')
                              : t('status.pending')}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      {booking.status !== 'confirmed' && (
                        <button
                          type="button"
                          onClick={() => onConfirm(booking.id)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold shadow-sm transition-colors',
                            booking.status === 'pending'
                              ? 'bg-maia text-white hover:bg-maia-dark'
                              : 'bg-cord text-ink hover:bg-cord/80'
                          )}
                        >
                          <Check className="h-3.5 w-3.5" aria-hidden="true" />
                          {t('zone.bookings.confirm')}
                        </button>
                      )}
                      {booking.status !== 'cancelled' && (
                        <button
                          type="button"
                          onClick={() => onCancel(booking.id)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold shadow-sm transition-colors',
                            booking.status === 'pending'
                              ? 'bg-pizza text-white hover:bg-pizza-dark'
                              : 'bg-cord text-ink hover:bg-cord/80'
                          )}
                        >
                          <X className="h-3.5 w-3.5" aria-hidden="true" />
                          {t('zone.bookings.cancel')}
                        </button>
                      )}
                      {booking.status === 'cancelled' && (
                        <button
                          type="button"
                          onClick={() => onDelete(booking.id)}
                          aria-label={t('zone.bookings.delete')}
                          className="inline-flex items-center rounded-full bg-ink/10 p-1.5 text-ink transition-colors hover:bg-pizza hover:text-white"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <BookingDetailModal
          booking={detailBooking}
          isOpen={detailBooking !== null}
          onClose={() => setDetailBooking(null)}
          t={t}
        />
      </>
      ))}
    </div>
  )
}

function MobileBookingCard({
  booking,
  t,
  onConfirm,
  onCancel,
  onDelete,
  onShowDetail,
}: {
  booking: Booking
  t: (key: string) => string
  onConfirm: (id: string) => void
  onCancel: (id: string) => void
  onDelete: (id: string) => void
  onShowDetail: (booking: Booking) => void
}) {
  const statusDotClass =
    booking.status === 'confirmed'
      ? 'bg-maia'
      : booking.status === 'cancelled'
        ? 'bg-pizza'
        : 'bg-gray-400'

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-cord bg-white px-3 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex flex-col">
          <span className="font-display text-lg font-bold leading-none text-ink">
            {booking.time}
          </span>
          <span className="mt-1 text-xs text-muted">
            {booking.guests}{' '}
            {booking.guests === 1
              ? t('zone.bookings.coverSingular') ?? 'coperto'
              : t('zone.bookings.coverPlural') ?? 'coperti'}
          </span>
        </div>
        <span
          className={cn(
            'h-2.5 w-2.5 rounded-full shrink-0',
            statusDotClass
          )}
          aria-hidden="true"
        />
      </div>

      <div className="flex items-center gap-1">
        {booking.status !== 'confirmed' && (
          <button
            type="button"
            onClick={() => onConfirm(booking.id)}
            aria-label={t('zone.bookings.confirm')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-maia text-white shadow-sm transition-colors hover:bg-maia-dark"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        {booking.status !== 'cancelled' && (
          <button
            type="button"
            onClick={() => onCancel(booking.id)}
            aria-label={t('zone.bookings.cancel')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-pizza text-white shadow-sm transition-colors hover:bg-pizza-dark"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        {booking.status === 'cancelled' && (
          <button
            type="button"
            onClick={() => onDelete(booking.id)}
            aria-label={t('zone.bookings.delete')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink/10 text-ink transition-colors hover:bg-pizza hover:text-white"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onShowDetail(booking)}
          aria-label={t('zone.bookings.showDetail')}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-cream text-ink shadow-sm transition-colors hover:bg-cord"
        >
          <Info className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

function Placeholder({ t }: { t: (key: string) => string }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center text-center">
      <p className="text-muted">{t('zone.placeholder')}</p>
    </div>
  )
}
